import { readPolicyIdsParam, writePolicyIdsParam } from "@/modules/training/public";
import { normalizedPolicyIds, type EvaluationViewMode } from "./evaluationResultsWindow";

export const EVALUATION_PATH = "/training/evaluation";
export const EVALUATION_RESULTS_TAB = "results";

// 활성 Evaluation Results 창의 정책 조합을 URL 에 반영하는 키다(useResultsWindowUrlSync).
// 창 identity 만 URL 에 담는다. 창 내부 뷰(칩·detail/compare·초점 카드)는 창 로컬 상태로 남는다.
// 정리와 상한 절단은 ?policy= 와 같은 코덱을 쓴다. 정렬은 창 key 의 정규형을 그대로 쓴다.
// 그래서 URL 값과 창이 항상 같은 형태가 된다.
export const resultsPolicySearchParamKey = "results_policy";

export function readResultsPolicyIds(params: URLSearchParams): string[] {
  return normalizedPolicyIds(readPolicyIdsParam(params, resultsPolicySearchParamKey));
}

export function writeResultsPolicyIdsParam(
  params: URLSearchParams,
  ids: readonly string[],
): URLSearchParams {
  return writePolicyIdsParam(params, resultsPolicySearchParamKey, ids);
}

// 결과 뷰를 탭과 URL 로 표현하던 구조의 쿼리 키다. 이전 딥링크 리더(useLegacyResultsDeepLink)가
// 창을 자동으로 열 때 한 번 읽고 정리하는 데만 쓴다. 현재 URL 표현은 위의 results_policy 뿐이다.
const TAB_PARAM = "tab";
const VIEW_PARAM = "view";
const EVAL_RUN_PARAM = "eval_run";
const MAX_VIEW_RUNS = 15;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// 결과 탭 구조의 Evaluation Results 탭 값이다. policies 쪽 이전 값(evaluation)은 기본 화면이라
// 정리만 한다.
const LEGACY_RESULTS_TAB_VALUES = [EVALUATION_RESULTS_TAB, "evaluation-analysis"];

// 결과 탭 칩이 쓰던 쿼리 키다. 창의 칩은 로컬 상태라서 이 키들은 정리 대상일 뿐이다.
const LEGACY_FILTER_PARAM_KEYS = [
  "run_policy",
  "session",
  "branch",
  "hash",
  "dataset",
  "task",
  "docker_tag",
  "status",
];

export type EvaluationViewState = {
  mode: EvaluationViewMode;
  evalRunIds: string[];
};

export type ParsedEvaluationView = {
  state: EvaluationViewState | null;
  valid: boolean;
};

export function parseEvaluationView(params: URLSearchParams): ParsedEvaluationView {
  const rawMode = params.get(VIEW_PARAM);
  const rawIds = params.getAll(EVAL_RUN_PARAM);
  if (rawMode === null && rawIds.length === 0) {
    return { state: null, valid: true };
  }
  if (!isEvaluationViewMode(rawMode) || rawIds.some((id) => !UUID_PATTERN.test(id))) {
    return { state: null, valid: false };
  }

  const evalRunIds = [...new Set(rawIds)];
  const expectedCount = rawMode === "detail" ? 1 : 2;
  const validCount =
    rawMode === "detail"
      ? evalRunIds.length === expectedCount
      : evalRunIds.length >= expectedCount && evalRunIds.length <= MAX_VIEW_RUNS;
  if (!validCount) {
    return { state: null, valid: false };
  }
  return { state: { mode: rawMode, evalRunIds }, valid: true };
}

// 이전 URL 이 결과 화면(이전 Results 탭 또는 뷰 팝업)을 가리키는지 판정한다. 리더는 이때만 창을 연다.
export function isLegacyResultsEntry(params: URLSearchParams): boolean {
  const tab = params.get(TAB_PARAM);
  return (
    (tab !== null && LEGACY_RESULTS_TAB_VALUES.includes(tab)) ||
    params.has(VIEW_PARAM) ||
    params.has(EVAL_RUN_PARAM)
  );
}

// 정리할 이전 키가 하나라도 남아 있는지 판정한다. tab 은 값과 관계없이 이전 키다(policies 쪽 값 포함).
export function hasLegacyResultsParams(params: URLSearchParams): boolean {
  return (
    params.has(TAB_PARAM) ||
    params.has(VIEW_PARAM) ||
    params.has(EVAL_RUN_PARAM) ||
    LEGACY_FILTER_PARAM_KEYS.some((key) => params.has(key))
  );
}

// 이전 키를 모두 지운다. ?policy=·?scope= 같은 현재 페이지 키는 바꾸지 않는다.
export function clearLegacyResultsParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(TAB_PARAM);
  next.delete(VIEW_PARAM);
  next.delete(EVAL_RUN_PARAM);
  for (const key of LEGACY_FILTER_PARAM_KEYS) {
    next.delete(key);
  }
  return next;
}

function isEvaluationViewMode(value: string | null): value is EvaluationViewMode {
  return value === "detail" || value === "compare" || value === "merge";
}
